import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { postsService } from '../../services/posts';
import { cityService } from '../../services/api';
import { CreatePostDto, UpdatePostDto, PostStatus } from '../../types/post';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import MultiImageUpload from '../../components/ui/MultiImageUpload';
import { useUserPermissions } from '../../hooks/useUserPermissions';

const PostFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const { isSuperAdmin } = useUserPermissions();

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    images: [] as string[],
    cityId: '',
    status: PostStatus.Published
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Buscar post para edição
  const { data: post, isLoading: postLoading } = useQuery({
    queryKey: ['post', id],
    queryFn: () => postsService.getPostById(id!),
    enabled: isEdit && !!id,
  });

  // Buscar cidades
  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ['cities'],
    queryFn: cityService.getAllCities,
  });

  // Preencher formulário quando carregar post para edição
  useEffect(() => {
    if (post) {
      setFormData({
        title: post.title,
        content: post.content,
        summary: post.summary,
        images: post.images || [],
        cityId: post.cityId || '',
        status: post.status
      });
    }
  }, [post]);

  // Criar post
  const createMutation = useMutation({
    mutationFn: (data: CreatePostDto) => postsService.createPost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      alert('Post criado com sucesso!');
      navigate('/posts');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao criar post');
    },
  });

  // Atualizar post
  const updateMutation = useMutation({
    mutationFn: (data: UpdatePostDto) => postsService.updatePost(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['post', id] });
      alert('Post atualizado com sucesso!');
      navigate('/posts');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Erro ao atualizar post');
    },
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Conteúdo é obrigatório';
    }

    if (!formData.summary.trim()) {
      newErrors.summary = 'Resumo é obrigatório';
    }

    if (formData.summary.length > 200) {
      newErrors.summary = 'Resumo deve ter no máximo 200 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      summary: formData.summary.trim(),
      images: formData.images,
      // Apenas SuperAdmin pode definir cityId, PlaceAdmin será definido automaticamente pelo backend
      ...(isSuperAdmin && { cityId: formData.cityId || undefined })
    };

    if (isEdit) {
      updateMutation.mutate({
        ...submitData,
        id: id!,
        status: formData.status
      });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleImagesChange = (images: string[]) => {
    setFormData({ ...formData, images });
  };

  const isLoading = postLoading || citiesLoading;
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/posts')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Voltar
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Editar Post' : 'Novo Post'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Atualize as informações do post' : 'Crie um novo post'}
            </p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="p-6 space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite o título do post"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Resumo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resumo * ({formData.summary.length}/200)
              </label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.summary ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite um resumo do post (máximo 200 caracteres)"
              />
              {errors.summary && (
                <p className="mt-1 text-sm text-red-600">{errors.summary}</p>
              )}
            </div>

            {/* Conteúdo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conteúdo *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.content ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Digite o conteúdo completo do post"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content}</p>
              )}
            </div>

            {/* Imagens */}
            <MultiImageUpload
              onImagesUploaded={handleImagesChange}
              currentImages={formData.images}
              maxImages={5}
            />

            {/* Cidade (apenas para SuperAdmin) */}
            {isSuperAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cidade
                </label>
                <select
                  value={formData.cityId}
                  onChange={(e) => setFormData({ ...formData, cityId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Global (sem cidade específica)</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Deixe vazio para posts globais (visíveis em todas as cidades)
                </p>
              </div>
            )}

            {/* Informação para PlaceAdmin */}
            {!isSuperAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Este post será automaticamente associado à cidade da sua cafeteria.
                </p>
              </div>
            )}

            {/* Status (apenas na edição) */}
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: Number(e.target.value) as PostStatus })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={PostStatus.Draft}>Rascunho</option>
                  <option value={PostStatus.Published}>Publicado</option>
                  <option value={PostStatus.Archived}>Arquivado</option>
                </select>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/posts')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEdit ? 'Atualizar' : 'Criar'} Post
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PostFormPage;
